@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import com.fsryan.foundation.data.FSSyncEvent
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The Stripe operations (through the Firebase callables until the API hosts them).
 *
 * One nested sealed triple per operation (sync data source playbook, Step 2). Every event carries
 * the operation's inputs and nothing it produced: outcomes are written to a local data source and
 * observed there. `Failure` adds the error. Constructors are public because the backend variant
 * module constructs these events (controlled-construction playbook: sync-event lifecycle variants).
 */
sealed interface WMBillingSyncEvent<out E: WMBillingSyncEvent<E>>: FSSyncEvent<E> {

    /** The plan prices; they land in the global local context. */
    sealed interface FetchPrices<out E: FetchPrices<E>>: WMBillingSyncEvent<E> {
        data class Initial(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchPrices<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchPrices<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchPrices<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Create a Checkout session for [lookupKey]; the redirect URL lands in the global local context under [lookupKey]. */
    sealed interface StartCheckout<out E: StartCheckout<E>>: WMBillingSyncEvent<E> {
        val lookupKey: String

        data class Initial(
            override val lookupKey: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): StartCheckout<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val lookupKey: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): StartCheckout<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val lookupKey: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): StartCheckout<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** Create a billing-portal session; the redirect URL lands in the global local context. */
    sealed interface OpenBillingPortal<out E: OpenBillingPortal<E>>: WMBillingSyncEvent<E> {
        val returnPath: String?

        data class Initial(
            override val returnPath: String?,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): OpenBillingPortal<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val returnPath: String?,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): OpenBillingPortal<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val returnPath: String?,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): OpenBillingPortal<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }

    /** What a completed checkout bought; it lands in the global local context under [sessionId]. */
    sealed interface FetchCheckoutSummary<out E: FetchCheckoutSummary<E>>: WMBillingSyncEvent<E> {
        val sessionId: String

        data class Initial(
            override val sessionId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchCheckoutSummary<Initial>, FSSyncEvent.Initial<Initial> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Success(
            override val sessionId: String,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchCheckoutSummary<Success>, FSSyncEvent.Success<Success> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        data class Failure(
            override val sessionId: String,
            override val error: Throwable,
            override val sequenceId: Uuid,
            override val extendedProperties: Map<String, Any>
        ): FetchCheckoutSummary<Failure>, FSSyncEvent.Error<Failure> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }
    }
}
